import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Switch } from '@/components/ui/switch';
import { Badge } from '@/components/ui/badge';
import { ImageIcon, Package, Plus, Upload, X } from 'lucide-react';
// Drag & Drop temporarily disabled - package not installed
import { Product, ProductComponent, Items, PromoGroup, Uploads } from '@/api/entities';
import { Items as ItemsAPI } from '@/api/client';
import { resolveAssetUrl } from '@/api/client';

const generateLocalId = () => {
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
    return crypto.randomUUID();
  }
  return `tmp-${Math.random().toString(36).slice(2, 11)}`;
};

export default function ProductForm({ product, onSave, onClose, isOpen }) {
  const { t } = useTranslation();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isUploadingImage, setIsUploadingImage] = useState(false);
  const [bookEditions, setBookEditions] = useState([]);
  const [promoGroups, setPromoGroups] = useState([]);
  const [components, setComponents] = useState([]);
  const [error, setError] = useState('');
  const [simpleProducts, setSimpleProducts] = useState([]);

  const [formData, setFormData] = useState({});
  const [resolvedImages, setResolvedImages] = useState([]);

  // Загружаем данные при открытии формы
  useEffect(() => {
    if (isOpen) {
      setError(''); // Очищаем ошибки при открытии
      fetchData();
      
      if (product) {
        // Редактируем существующий товар
        loadProductData();
      } else {
        // Создаем новый товар - сбрасываем форму
        resetForm();
      }
    }
  }, [isOpen, product]);

  const fetchData = async () => {
    try {
      console.log('Fetching data for ProductForm...');
      
      const [editionsData, promoData, simpleProductsData] = await Promise.all([
        Items.list({ type: 'book_edition' }),
        PromoGroup.list(),
        Items.list({ type: 'simple_product' })
      ]);
      
      console.log('BookEditions loaded:', editionsData);
      console.log('BookEditions sample:', editionsData?.[0]);
      console.log('PromoGroups loaded:', promoData);
      console.log('SimpleProducts loaded:', simpleProductsData);
      console.log('SimpleProducts length:', simpleProductsData?.length || 0);
      
      setBookEditions(editionsData || []);
      setPromoGroups(promoData || []);
      setSimpleProducts(simpleProductsData || []);
      
      // Fallback для тестирования если API не работает
      if (!editionsData || editionsData.length === 0) {
        console.warn('No book editions loaded, using fallback data');
        setBookEditions([
          {
            id: 'test-1',
            name: 'Test Edition 1',
            isbn: '1234567890',
            book_title: 'Test Book 1'
          },
          {
            id: 'test-2', 
            name: 'Test Edition 2',
            isbn: '0987654321',
            book_title: 'Test Book 2'
          }
        ]);
      }
      
      // Fallback для простых товаров
      if (!simpleProductsData || simpleProductsData.length === 0) {
        console.warn('No products loaded, using fallback data');
        setSimpleProducts([
          {
            id: 'prod-1',
            name: 'Test Product 1',
            sku: 'TEST-001',
            type: 'simple_product'
          },
          {
            id: 'prod-2',
            name: 'Test Product 2', 
            sku: 'TEST-002',
            type: 'simple_product'
          }
        ]);
      }
    } catch (error) {
      console.error('Error fetching data:', error);
    }
  };

  const galleryImages = resolvedImages.length > 0
    ? resolvedImages
    : (formData.cover_image_url
        ? [{
            url: formData.cover_image_url,
            alt: formData.name || t('product.image'),
            order: 0,
            isCoverFallback: true,
            isVirtualFallback: true
          }]
        : []);

  const loadProductData = async () => {
    if (!product) return;

    const rawCoverUrl = product.cover_image_url || product.metadata?.cover_image_url || '';
    let resolvedCoverUrl = rawCoverUrl;
    if (rawCoverUrl) {
      try {
        resolvedCoverUrl = await resolveAssetUrl(rawCoverUrl);
      } catch (error) {
        console.warn('[ProductForm] Failed to resolve cover image url:', error);
      }
    }

    const sourceImages = (Array.isArray(product.images) && product.images.length > 0)
      ? product.images
      : (Array.isArray(product.metadata?.images) ? product.metadata.images : []);
    const normalizedImages = (sourceImages || []).map((img, idx) => ({
      ...img,
      order: typeof img.order === 'number' ? img.order : idx
    }));

    if (normalizedImages.length === 0 && resolvedCoverUrl) {
      normalizedImages.push({
        url: resolvedCoverUrl,
        alt: product.name || t('product.product_name'),
        order: 0,
        isCoverFallback: true
      });
    }

    setFormData({
      name: product.name || '',
      description: product.description || '',
      sku: product.sku || '',
      price: product.price || 0,
      type: product.type || 'simple_product',
      is_active: product.is_active !== false,
      is_allowed_for_sale: product.is_allowed_for_sale === true,
      is_stock_control_enabled: product.type === 'simple_product'
        ? product.is_stock_control_enabled !== false
        : true,
      show_composition_in_storefront: product.type === 'bundle' ? (product.show_composition_in_storefront !== false) : false,
      promo_group_ids: (product.promo_group_ids && product.promo_group_ids.length > 0)
        ? product.promo_group_ids
        : (Array.isArray(product.metadata?.promo_group_ids) ? product.metadata.promo_group_ids : []),
      images: normalizedImages,
      cover_image_url: resolvedCoverUrl,
      // Поля для простых товаров
      brand: product.metadata?.brand || '',
      color: product.metadata?.color || '',
      size: product.metadata?.size || '',
      material: product.metadata?.material || '',
      weight: product.metadata?.weight !== undefined && product.metadata?.weight !== null ? product.metadata.weight : '',
      dimensions: product.metadata?.dimensions || '',
      country: product.metadata?.country || '',
      warranty: product.metadata?.warranty || '',
      // Поля для книг
      isbn: product.metadata?.isbn || '',
      author: product.metadata?.author || '',
      publisher: product.metadata?.publisher || '',
      format: product.metadata?.format || '',
      pages: product.metadata?.pages || 0,
      item_id: product.item_id || '',
      // Поля для наборов
      is_bundle: product.is_bundle || false,
      ribbon_top_left: product.ribbon_top_left || { text: '', background_color: '#e53e3e', text_color: '#ffffff', width: 120, font_size: 12, font_weight: 'bold' },
      ribbon_top_right: product.ribbon_top_right || { text: '', background_color: '#38a169', text_color: '#ffffff', width: 120, font_size: 12, font_weight: 'bold' },
      label_bottom_left: product.label_bottom_left || { text: '', background_color: '#3182ce', text_color: '#ffffff', font_size: 11, font_weight: 'normal', padding: 6 },
      label_bottom_right: product.label_bottom_right || { text: '', background_color: '#d69e2e', text_color: '#ffffff', font_size: 11, font_weight: 'normal', padding: 6 },
    });

    // Загружаем компоненты
    if (product.id) {
      try {
        let existingComponents = [];
        try {
          existingComponents = await ProductComponent.get(product.id);
        } catch (error) {
          if (error?.message?.includes('404')) {
            console.log('No components found for product:', product.id);
            existingComponents = [];
          } else {
            console.error('Failed to load components for product:', product.id, error);
          }
        }

        console.log('Loaded existing components:', existingComponents);
        
        // Преобразуем компоненты в формат для формы
        const formattedComponents = (existingComponents || []).map(comp => ({
          id: comp.id,
          localId: comp.id ?? generateLocalId(),
          type: comp.book_edition_id ? 'book_edition' : 'simple_product',
          item_id: comp.book_edition_id || '',
          product_id: comp.single_product_id || '',
          quantity: comp.quantity
        }));
        
        console.log('Formatted components:', formattedComponents);
        setComponents(formattedComponents || []);
      } catch (error) {
        console.error('Error loading components:', error);
      }
    }
  };

  const resetForm = () => {
    setFormData({
      name: '',
      description: '',
      sku: '',
      price: '',
      type: 'simple_product', // По умолчанию одиночный товар
      is_active: true,
      is_allowed_for_sale: false,
      is_stock_control_enabled: true,
      show_composition_in_storefront: false,
      promo_group_ids: [],
      images: [],
      cover_image_url: '',
      // Поля для простых товаров
      brand: '',
      color: '',
      size: '',
      material: '',
      weight: '',
      dimensions: '',
      country: '',
      warranty: '',
      // Поля для книг (больше не используются)
      isbn: '',
      author: '',
      publisher: '',
      format: '',
      pages: '',
      item_id: '',
      // Поля для наборов
      is_bundle: false,
      ribbon_top_left: { text: '', background_color: '#e53e3e', text_color: '#ffffff', width: 120, font_size: 12, font_weight: 'bold' },
      ribbon_top_right: { text: '', background_color: '#38a169', text_color: '#ffffff', width: 120, font_size: 12, font_weight: 'bold' },
      label_bottom_left: { text: '', background_color: '#3182ce', text_color: '#ffffff', font_size: 11, font_weight: 'normal', padding: 6 },
      label_bottom_right: { text: '', background_color: '#d69e2e', text_color: '#ffffff', font_size: 11, font_weight: 'normal', padding: 6 },
    });
    setComponents([]);
    setError('');
  };

  const handleInputChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    
    // Если тип изменен на bundle, автоматически включаем показ состава
    if (field === 'type') {
      if (value === 'bundle') {
        setFormData(prev => ({ ...prev, show_composition_in_storefront: true }));
      }
      if (value === 'simple_product') {
        setFormData(prev => ({
          ...prev,
          is_stock_control_enabled: prev.is_stock_control_enabled !== false
        }));
      } else {
        setFormData(prev => ({
          ...prev,
          is_stock_control_enabled: true
        }));
      }
    }
    
    // Очищаем ошибку при изменении полей
    if (error) setError('');
  };

  const handleImageUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    setIsUploadingImage(true);
    try {
      console.log('[ProductForm] Upload start:', { name: file.name, size: file.size, type: file.type });
      const res = await Uploads.upload(file);
      console.log('[ProductForm] Upload response:', res);
      const rawUrl = res.file_url || `/api/v1/uploads/files/${res.filename}`;
      const serverUrl = await resolveAssetUrl(rawUrl);
      const newImage = {
        url: serverUrl,
        alt: file.name,
        order: (formData.images?.length || 0)
      };
      setFormData(prev => {
        const nextImages = [...(prev.images || []), newImage];
        const nextCover = nextImages[0]?.url || prev.cover_image_url;
        return {
          ...prev,
          images: nextImages,
          cover_image_url: nextCover
        };
      });
    } catch (error) {
      console.error('[ProductForm] Error uploading image:', error);
    } finally {
      setIsUploadingImage(false);
      // allow re-selecting the same file name
      try { if (e?.target) e.target.value = ''; } catch(_) {}
    }
  };

  // Нормализация URL для уже существующих изображений из БД
  useEffect(() => {
    const normalize = async () => {
      const imgs = Array.isArray(formData.images) ? formData.images : [];
      const mapped = [];
      for (const img of imgs) {
        const normalizedUrl = await resolveAssetUrl(img?.url || '');
        mapped.push({ ...img, url: normalizedUrl });
      }
      setResolvedImages(mapped);
    };
    normalize().catch(() => setResolvedImages(Array.isArray(formData.images) ? formData.images : []));
  }, [formData.images]);

  const removeImage = (index) => {
    setFormData(prev => {
      const nextImages = (prev.images || []).filter((_, i) => i !== index);
      const reindexed = nextImages.map((img, idx) => ({ ...img, order: idx }));
      return {
        ...prev,
        images: reindexed,
        cover_image_url: reindexed[0]?.url || ''
      };
    });
  };

  // Drag & Drop temporarily disabled
  // const handleImageDragEnd = (result) => {
  //   if (!result.destination) return;
  //   const items = Array.from(formData.images);
  //   const [reorderedItem] = items.splice(result.source.index, 1);
  //   items.splice(result.destination.index, 0, reorderedItem);
  //   setFormData(prev => ({ ...prev, images: items }));
  // };

  const addComponent = () => {
    console.log('Adding new component...');
    const newComponent = {
      id: null,
      localId: generateLocalId(),
      type: 'book_edition', // По умолчанию книга
      item_id: '',
      product_id: '', // Для простых товаров
      quantity: 1
    };
    console.log('New component:', newComponent);
    setComponents(prev => [...prev, newComponent]);
  };

  const removeComponent = (index) => {
    setComponents(prev => prev.filter((_, i) => i !== index));
  };

  const updateComponent = (index, field, value) => {
    setComponents(prev => prev.map((comp, i) => 
      i === index ? { ...comp, [field]: value } : comp
    ));
  };

  const getEditionDisplay = (editionId) => {
    const edition = bookEditions.find(e => e.id === editionId);
    if (!edition) return 'Unknown Edition';
    // В новой системе items, ISBN находится в edition.metadata.isbn
    const isbn = edition.metadata?.isbn || 'No ISBN';
    return `${edition.name} (${isbn})`;
  };

  const getFilteredEditionsForSelect = (excludeIndex) => {
    console.log('getFilteredEditionsForSelect called with excludeIndex:', excludeIndex);
    console.log('Current bookEditions:', bookEditions);
    console.log('Current components:', components);
    
    const usedEditions = components
              .map((comp, index) => index !== excludeIndex ? comp.item_id : null)
      .filter(Boolean);

    console.log('Used editions:', usedEditions);

    const grouped = {};
    bookEditions.forEach(edition => {
      console.log('Processing edition:', edition);
      console.log('Edition name:', edition.name);
      console.log('Edition metadata:', edition.metadata);
      console.log('Edition metadata.isbn:', edition.metadata?.isbn);
      
      if (!usedEditions.includes(edition.id)) {
        // В новой системе items, название книги - это edition.name
        // ISBN находится в edition.metadata.isbn
        const bookTitle = edition.name || 'Unknown Book';
        const isbn = edition.metadata?.isbn || 'No ISBN';
        
        console.log('Book title:', bookTitle);
        console.log('ISBN:', isbn);
        
        if (!grouped[bookTitle]) {
          grouped[bookTitle] = { bookTitle, editions: [] };
        }
        grouped[bookTitle].editions.push({
          id: edition.id,
          display: `${edition.name} (${isbn})`
        });
      }
    });

    console.log('Final grouped result:', grouped);
    return grouped;
  };

  const getFilteredProductsForSelect = (excludeIndex) => {
    console.log('getFilteredProductsForSelect called with excludeIndex:', excludeIndex);
    console.log('Current components:', components);
    console.log('Current simpleProducts:', simpleProducts);
    
    const usedProducts = components
      .map((comp, index) => index !== excludeIndex ? comp.product_id : null)
      .filter(Boolean);
    
    console.log('Used products:', usedProducts);
    
    const filtered = simpleProducts.filter(product => !usedProducts.includes(product.id));
    console.log('Filtered products:', filtered);
    
    return filtered;
  };

  const validateForm = () => {
    if (!formData.name || !formData.price) {
      return false;
    }
    return true;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);

    if (!validateForm()) {
      setIsSubmitting(false);
      return;
    }

    // Создаем metadata в зависимости от типа товара
    let metadata = {};
    
    // Вес доступен для всех типов товаров
    const weightValue = formData.weight !== '' && formData.weight !== undefined && formData.weight !== null
      ? parseInt(formData.weight, 10)
      : undefined;
    
    if (formData.type === 'simple_product') {
      metadata = {
        brand: formData.brand || undefined,
        color: formData.color || undefined,
        size: formData.size || undefined,
        material: formData.material || undefined,
        weight: weightValue,
        dimensions: formData.dimensions || undefined,
        country: formData.country || undefined,
        warranty: formData.warranty || undefined
      };
      // Убираем undefined значения
      Object.keys(metadata).forEach(key => {
        if (metadata[key] === undefined || metadata[key] === '') {
          delete metadata[key];
        }
      });
    } else if (formData.type === 'book_edition') {
      metadata = {
        isbn: formData.isbn,
        author: formData.author,
        publisher: formData.publisher,
        format: formData.format,
        pages: formData.pages,
        weight: weightValue
      };
      // Убираем undefined значения
      if (metadata.weight === undefined) {
        delete metadata.weight;
      }
    } else if (formData.type === 'bundle') {
      metadata = {
        weight: weightValue
      };
      // Убираем undefined значения
      if (metadata.weight === undefined) {
        delete metadata.weight;
      }
    }

    // Общие данные изображений
    const normalizedImages = (formData.images || []).map((img, idx) => ({
      url: img.url,
      alt: img.alt,
      order: typeof img.order === 'number' ? img.order : idx
    }));
    const coverUrl = formData.cover_image_url || (normalizedImages[0]?.url || undefined);

    const productData = {
      type: formData.type,
      name: formData.name,
      description: formData.description,
      sku: formData.sku,
      ...(formData.price && formData.price > 0 && { price: Number(formData.price) }),
      is_active: formData.is_active,
      is_allowed_for_sale: formData.is_allowed_for_sale,
      is_stock_control_enabled: formData.type === 'simple_product'
        ? formData.is_stock_control_enabled !== false
        : true,
      show_composition_in_storefront: formData.show_composition_in_storefront,
      images: normalizedImages,
      cover_image_url: coverUrl,
      promo_group_ids: formData.promo_group_ids,
      metadata: { 
        ...metadata, 
        images: normalizedImages, 
        cover_image_url: coverUrl 
      },
      // Поля для наборов (если это bundle)
      ...(formData.type === 'bundle' && {
        ribbon_top_left: formData.ribbon_top_left.text ? formData.ribbon_top_left : undefined,
        ribbon_top_right: formData.ribbon_top_right.text ? formData.ribbon_top_right : undefined,
        label_bottom_left: formData.label_bottom_left.text ? formData.label_bottom_left : undefined,
        label_bottom_right: formData.label_bottom_right.text ? formData.label_bottom_right : undefined,
      })
    };

    try {
      let savedProduct;
      if (product) {
        console.log('Updating existing product:', product.id);
        savedProduct = await Product.update(product.id, productData);
      } else {
        console.log('Creating new product');
        savedProduct = await Product.create(productData);
      }
      
      console.log('Product saved successfully:', savedProduct);

      // Синхронизируем компоненты только для наборов
      if (formData.type === 'bundle') {
        // Если вес не был указан вручную - пересчитать после сохранения компонентов
        const shouldRecalculateWeight = !formData.weight || formData.weight === '' || formData.weight === undefined || formData.weight === null;
        const existingComponents = product ? await ProductComponent.get(savedProduct.id) : [];
        const componentIdsToKeep = [];

        for (const component of components) {
          console.log('Processing component:', component);
          
          if (!component.quantity || component.quantity < 1) {
            console.log('Skipping component - invalid quantity');
            continue;
          }

                  // Проверяем что выбран либо item_id, либо product_id
        if (component.type === 'book_edition' && !component.item_id) {
            console.log('Skipping book_edition component - no item_id');
            continue;
        }
          if (component.type === 'simple_product' && !component.product_id) {
            console.log('Skipping simple_product component - no product_id');
            continue;
          }

          let componentData = {
            product_id: savedProduct.id,
            quantity: component.quantity
          };

          // Добавляем соответствующий ID в зависимости от типа
          if (component.type === 'book_edition') {
                    componentData.book_edition_id = component.item_id;
        console.log('Adding book_edition_id:', component.item_id);
          } else if (component.type === 'simple_product') {
            componentData.single_product_id = component.product_id;
            console.log('Adding single_product_id:', component.product_id);
          }
          
                  console.log('Final componentData:', componentData);

        if (component.id) {
          // Обновляем существующий компонент
          // Нужно сохранить существующий тип и ID
          const existingComponent = existingComponents.find(c => c.id === component.id);
          if (existingComponent) {
            // Сохраняем существующий тип компонента
                    if (existingComponent.book_edition_id) {
            componentData.book_edition_id = existingComponent.book_edition_id;
        } else if (existingComponent.single_product_id) {
              componentData.single_product_id = existingComponent.single_product_id;
            }
          }
          console.log('Updating existing component:', component.id, componentData);
          await ProductComponent.update(component.id, componentData);
          componentIdsToKeep.push(component.id);
        } else {
          // Создаем новый компонент
          console.log('Creating new component:', componentData);
          const newComponent = await ProductComponent.create(componentData);
          console.log('New component created:', newComponent);
          componentIdsToKeep.push(newComponent.id);
        }
        }

        // Удаляем компоненты, которых больше нет
        for (const existingComponent of existingComponents) {
          if (!componentIdsToKeep.includes(existingComponent.id)) {
            await ProductComponent.delete(existingComponent.id);
          }
        }
        
        // Если вес не был указан вручную - пересчитать после сохранения компонентов
        if (shouldRecalculateWeight) {
          try {
            const result = await ItemsAPI.calculateWeight(savedProduct.id);
            if (result && result.item) {
              savedProduct = result.item;
              console.log('Bundle weight recalculated:', result.weight);
            }
          } catch (error) {
            console.warn('Could not recalculate bundle weight:', error);
            // Не критично, вес может быть рассчитан при следующем обновлении
          }
        }
      }

      onSave(savedProduct);
      onClose();
    } catch (error) {
      console.error('Error saving product:', error);
      // Показываем ошибку пользователю
      setError(error.message || 'Error saving product');
    } finally {
      setIsSubmitting(false);
    }
  };

  if (!isOpen) return null;

  // Если форма еще не инициализирована, показываем загрузку
  if (Object.keys(formData).length === 0) {
    return (
      <Dialog open={isOpen} onOpenChange={onClose}>
        <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
          <div className="flex items-center justify-center py-12">
            <div className="text-center">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-amber-600 mx-auto mb-4"></div>
              <p className="text-slate-500">Loading form...</p>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    );
  }

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>
            {product ? t('product.edit_product') : t('product.create_product')}
          </DialogTitle>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="space-y-6">
          {/* Отображение ошибки */}
          {error && (
            <div className="p-4 bg-red-50 border border-red-200 rounded-lg">
              <div className="flex items-center gap-2">
                <div className="w-5 h-5 bg-red-500 rounded-full flex items-center justify-center">
                  <span className="text-white text-xs font-bold">!</span>
                </div>
                <div className="flex-1">
                  <p className="text-sm font-medium text-red-800">
                    {t('errors.general_error')}
                  </p>
                  <p className="text-sm text-red-600 mt-1">
                    {error}
                  </p>
                </div>
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  onClick={() => setError('')}
                  className="text-red-500 hover:text-red-700"
                >
                  <X className="w-4 h-4" />
                </Button>
              </div>
            </div>
          )}

          {/* Тип товара */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <Label htmlFor="type">{t('product.type')}</Label>
              <Select value={formData.type} onValueChange={(value) => handleInputChange('type', value)}>
                <SelectTrigger>
                  <SelectValue placeholder={t('placeholders.general.select_type')} />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="simple_product">{t('product.simple_product')}</SelectItem>
                  <SelectItem value="book_edition">{t('product.book_edition')}</SelectItem>
                  <SelectItem value="bundle">{t('product.bundle')}</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>

          {/* Основная информация */}
          <Card>
            <CardHeader>
              <CardTitle>{t('product.basic_information')}</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                <div className="space-y-4">
                  <div>
                    <Label htmlFor="name">{t('product.product_name')} *</Label>
                    <Input
                      id="name"
                      value={formData.name}
                      onChange={(e) => handleInputChange('name', e.target.value)}
                      placeholder={t('placeholders.product.name')}
                      required
                    />
                  </div>

                  <div>
                    <Label htmlFor="sku">{t('product.sku')}</Label>
                    <Input
                      id="sku"
                      value={formData.sku}
                      onChange={(e) => handleInputChange('sku', e.target.value)}
                      placeholder={t('placeholders.product.sku')}
                    />
                  </div>

                  <div>
                    <Label htmlFor="price">{t('product.price')} *</Label>
                    <Input
                      id="price"
                      type="number"
                      step="0.01"
                      value={formData.price}
                      onChange={(e) => handleInputChange('price', parseFloat(e.target.value) || 0)}
                      placeholder={t('placeholders.product.price')}
                      required
                    />
                  </div>

                  <div>
                    <Label htmlFor="weight">{t('product.weight')} (г)</Label>
                    <Input
                      id="weight"
                      type="number"
                      step="1"
                      min="0"
                      value={formData.weight}
                      onChange={(e) => {
                        const value = e.target.value;
                        if (value === '' || value === undefined) {
                          handleInputChange('weight', '');
                        } else {
                          const intValue = parseInt(value, 10);
                          if (!isNaN(intValue) && intValue >= 0) {
                            handleInputChange('weight', intValue);
                          }
                        }
                      }}
                      placeholder={t('placeholders.product.weight')}
                    />
                  </div>

                  {/* Promo Groups */}
                  <div className="space-y-3">
                    <Label className="text-base font-semibold">{t('product.promo_groups')}</Label>
                    <p className="text-sm text-slate-600">
                      {t('product.promo_groups_description')}
                    </p>
                    
                    {promoGroups.length === 0 ? (
                      <div className="p-4 bg-slate-50 border border-slate-200 rounded-lg text-center">
                        <p className="text-sm text-slate-500">
                          {t('product.no_promo_groups')}
                        </p>
                      </div>
                    ) : (
                      <div className="space-y-2">
                        {promoGroups
                          .filter(group => group.is_active)
                          .sort((a, b) => a.priority - b.priority)
                          .map(group => (
                            <div key={group.id} className="flex items-center space-x-2">
                              <input
                                type="checkbox"
                                id={`promo-${group.id}`}
                                checked={(formData.promo_group_ids || []).includes(group.id)}
                                onChange={(e) => {
                                    const currentGroups = formData.promo_group_ids || [];
                                    if (e.target.checked) {
                                        handleInputChange('promo_group_ids', [...currentGroups, group.id]);
                                    } else {
                                        handleInputChange('promo_group_ids', currentGroups.filter(id => id !== group.id));
                                    }
                                }}
                                className="w-4 h-4 text-amber-600 bg-gray-100 border-gray-300 rounded focus:ring-amber-500"
                              />
                              <label 
                                  htmlFor={`promo-${group.id}`} 
                                  className="text-sm font-medium text-gray-900 cursor-pointer flex items-center gap-2"
                              >
                                  {group.name}
                                  <Badge className="bg-purple-100 text-purple-800 text-xs">
                                      {group.card_size_percent}%
                                  </Badge>
                              </label>
                            </div>
                          ))}
                      </div>
                    )}

                    {(formData.promo_group_ids || []).length > 0 && (
                      <div className="p-3 bg-amber-50 border border-amber-200 rounded-lg">
                        <p className="text-sm text-amber-800">
                          <strong>{t('product.selected_groups')}:</strong> {(formData.promo_group_ids || []).length}
                          <br />
                          {t('product.promo_groups_note')}
                        </p>
                      </div>
                    )}
                  </div>

                  <div className="flex items-center gap-2">
                    <Switch
                      id="is_active"
                      checked={formData.is_active}
                      onCheckedChange={(checked) => handleInputChange('is_active', checked)}
                    />
                    <Label htmlFor="is_active">{t('product.is_active')}</Label>
                  </div>
                <div className="flex items-center gap-2">
                  <Switch
                    id="is_allowed_for_sale"
                    checked={formData.is_allowed_for_sale}
                    onCheckedChange={(checked) => handleInputChange('is_allowed_for_sale', checked)}
                  />
                  <Label htmlFor="is_allowed_for_sale">{t('product.is_allowed_for_sale')}</Label>
                </div>
                {formData.type === 'simple_product' && (
                  <div className="flex items-center gap-2">
                    <Switch
                      id="is_stock_control_enabled"
                      checked={formData.is_stock_control_enabled !== false}
                      onCheckedChange={(checked) => handleInputChange('is_stock_control_enabled', checked)}
                    />
                    <Label htmlFor="is_stock_control_enabled">{t('product.is_stock_control_enabled')}</Label>
                  </div>
                )}

                  {formData.type === 'bundle' && (
                    <div className="flex items-center gap-2">
                      <Switch
                        id="show_composition_in_storefront"
                        checked={formData.show_composition_in_storefront}
                        onCheckedChange={(checked) => handleInputChange('show_composition_in_storefront', checked)}
                      />
                      <Label htmlFor="show_composition_in_storefront">{t('product.show_composition')}</Label>
                      <p className="text-xs text-slate-500 ml-2">{t('product.show_composition_note')}</p>
                    </div>
                  )}

                  <div>
                    <Label htmlFor="description">{t('product.description')}</Label>
                    <Textarea
                      id="description"
                      value={formData.description}
                      onChange={(e) => handleInputChange('description', e.target.value)}
                      placeholder={t('placeholders.product.description')}
                      className="h-24"
                    />
                  </div>
                </div>

                {/* Image Gallery Section */}
                <div className="space-y-4">
                  <Card>
                    <CardHeader className="flex flex-row items-center justify-between">
                      <CardTitle className="text-lg flex items-center gap-2">
                        <ImageIcon className="w-5 h-5 text-amber-600" />
                        {t('product.images')} ({formData.images.length}/10)
                      </CardTitle>
                      <div className="relative">
                        <input
                          type="file"
                          accept="image/*"
                          onChange={handleImageUpload}
                          className="absolute inset-0 w-full h-full opacity-0 cursor-pointer"
                          disabled={isUploadingImage || formData.images.length >= 10}
                        />
                        <Button
                          type="button"
                          variant="outline"
                          size="sm"
                          disabled={isUploadingImage || formData.images.length >= 10}
                        >
                          <Upload className="w-4 h-4 mr-2" />
                          {isUploadingImage ? t('notifications.info.uploading') : t('actions.upload')}
                        </Button>
                      </div>
                    </CardHeader>
                    <CardContent>
                        {galleryImages.length === 0 ? (
                        <div className="text-center py-8 text-slate-500 border-2 border-dashed border-slate-200 rounded-lg">
                          <ImageIcon className="w-12 h-12 mx-auto mb-3 text-slate-300" />
                          <p className="font-medium">{t('product.no_images')}</p>
                          <p className="text-sm">{t('product.add_images_note')}</p>
                        </div>
                      ) : (
                        <div className="space-y-2">
                          {galleryImages.map((image, index) => (
                            <div key={`${image.url}-${index}`} className="flex items-center gap-3 p-3 bg-slate-50 rounded-lg">
                              <div className="cursor-move">
                                <Package className="w-4 h-4 text-slate-400" />
                              </div>
                              <img
                                src={image.url}
                                alt={image.alt}
                                className="w-16 h-16 object-cover rounded-md"
                                onError={(e) => {
                                  console.error('Failed to load image in gallery:', image.url);
                                  e.target.style.display = 'none';
                                }}
                                onLoad={() => {
                                  console.log('Gallery image loaded successfully:', image.url);
                                }}
                              />
                              <div className="flex-1">
                                <p className="text-sm font-medium text-slate-700">
                                  {t('product.image')} {index + 1}
                                </p>
                                <p className="text-xs text-slate-500">
                                  {index === 0 ? t('product.main_image') : t('product.additional_image')}
                                </p>
                              </div>
                              <div className="flex items-center gap-2">
                                {index !== 0 && !image.isVirtualFallback && (
                                  <Button
                                    type="button"
                                    variant="outline"
                                    size="sm"
                                    onClick={() => {
                                      setFormData(prev => {
                                        const images = [...(prev.images || [])];
                                        const [img] = images.splice(index, 1);
                                        images.unshift(img);
                                        const reindexed = images.map((it, i) => ({ ...it, order: i }));
                                        return { ...prev, images: reindexed, cover_image_url: reindexed[0]?.url };
                                      });
                                    }}
                                  >
                                    {t('actions.select')} {t('product.main_image')}
                                  </Button>
                                )}
                                {!image.isVirtualFallback && (
                                  <Button
                                    type="button"
                                    variant="ghost"
                                    size="icon"
                                    onClick={() => removeImage(index)}
                                    className="text-red-500 hover:text-red-700"
                                  >
                                    <X className="w-4 h-4" />
                                  </Button>
                                )}
                              </div>
                            </div>
                          ))}
                        </div>
                      )}
                    </CardContent>
                  </Card>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Поля для простых товаров */}
          {formData.type === 'simple_product' && (
            <Card>
              <CardHeader>
                <CardTitle>{t('product.simple_product_details')}</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                  <div className="space-y-4">
                    <div>
                      <Label htmlFor="brand">{t('product.brand')}</Label>
                      <Input
                        id="brand"
                        value={formData.brand}
                        onChange={(e) => handleInputChange('brand', e.target.value)}
                        placeholder={t('placeholders.product.brand')}
                      />
                    </div>
                    <div>
                      <Label htmlFor="color">{t('product.color')}</Label>
                      <Input
                        id="color"
                        value={formData.color}
                        onChange={(e) => handleInputChange('color', e.target.value)}
                        placeholder={t('placeholders.product.color')}
                      />
                    </div>
                    <div>
                      <Label htmlFor="size">{t('product.size')}</Label>
                      <Input
                        id="size"
                        value={formData.size}
                        onChange={(e) => handleInputChange('size', e.target.value)}
                        placeholder={t('placeholders.product.size')}
                      />
                    </div>
                    <div>
                      <Label htmlFor="material">{t('product.material')}</Label>
                      <Input
                        id="material"
                        value={formData.material}
                        onChange={(e) => handleInputChange('material', e.target.value)}
                        placeholder={t('placeholders.product.material')}
                      />
                    </div>
                  </div>
                  <div className="space-y-4">
                    <div>
                      <Label htmlFor="dimensions">{t('product.dimensions')}</Label>
                      <Input
                        id="dimensions"
                        value={formData.dimensions}
                        onChange={(e) => handleInputChange('dimensions', e.target.value)}
                        placeholder={t('placeholders.product.dimensions')}
                      />
                    </div>
                    <div>
                      <Label htmlFor="country">{t('product.country')}</Label>
                      <Input
                        id="country"
                        value={formData.country}
                        onChange={(e) => handleInputChange('country', e.target.value)}
                        placeholder={t('placeholders.product.country')}
                      />
                    </div>
                    <div>
                      <Label htmlFor="warranty">{t('product.warranty')}</Label>
                      <Input
                        id="warranty"
                        value={formData.warranty}
                        onChange={(e) => handleInputChange('warranty', e.target.value)}
                        placeholder={t('placeholders.product.warranty')}
                      />
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>
          )}

          {/* Поля для книг */}
          {formData.type === 'book_edition' && (
            <Card>
              <CardHeader>
                <CardTitle>{t('product.book_details')}</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                  <div className="space-y-4">
                    <div>
                      <Label htmlFor="isbn">{t('product.isbn')}</Label>
                      <Input
                        id="isbn"
                        value={formData.isbn}
                        onChange={(e) => handleInputChange('isbn', e.target.value)}
                        placeholder={t('placeholders.product.isbn')}
                      />
                    </div>
                    <div>
                      <Label htmlFor="author">{t('product.author')}</Label>
                      <Input
                        id="author"
                        value={formData.author}
                        onChange={(e) => handleInputChange('author', e.target.value)}
                        placeholder={t('placeholders.product.author')}
                      />
                    </div>
                    <div>
                      <Label htmlFor="publisher">{t('product.publisher')}</Label>
                      <Input
                        id="publisher"
                        value={formData.publisher}
                        onChange={(e) => handleInputChange('publisher', e.target.value)}
                        placeholder={t('placeholders.product.publisher')}
                      />
                    </div>
                  </div>
                  <div className="space-y-4">
                    <div>
                      <Label htmlFor="format">{t('product.format')}</Label>
                      <Input
                        id="format"
                        value={formData.format}
                        onChange={(e) => handleInputChange('format', e.target.value)}
                        placeholder={t('placeholders.product.format')}
                      />
                    </div>
                    <div>
                      <Label htmlFor="pages">{t('product.pages')}</Label>
                      <Input
                        id="pages"
                        type="number"
                        value={formData.pages}
                        onChange={(e) => handleInputChange('pages', parseInt(e.target.value) || 0)}
                        placeholder={t('placeholders.product.pages')}
                      />
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>
          )}

          {/* Блок привязки издания удалён по требованиям */}

          {/* Состав для наборов */}
          {formData.type === 'bundle' && (
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Package className="w-5 h-5" />
                  {t('product.composition')}
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-4">
                  <div className="flex items-center justify-between">
                    <p className="text-sm text-slate-600">
                      {t('product.composition_description')}
                    </p>
                    <Button
                      type="button"
                      onClick={addComponent}
                      size="sm"
                      className="bg-amber-600 hover:bg-amber-700"
                    >
                      <Plus className="w-4 h-4 mr-2" />
                      {t('actions.add_component')}
                    </Button>
                  </div>

                  {components.length === 0 ? (
                    <div className="text-center py-6 text-slate-500 border-2 border-dashed border-slate-200 rounded-lg">
                      <Package className="w-12 h-12 mx-auto mb-3 text-slate-300" />
                      <p className="font-medium">{t('product.no_components')}</p>
                      <p className="text-sm">{t('product.add_components_note')}</p>
                    </div>
                  ) : (
                    <div className="space-y-3">
                      {components.map((component, index) => {
                        const componentKey = component.id || component.localId || `component-${index}`;
                        const availableEditions = getFilteredEditionsForSelect(index);
                        const availableProducts = getFilteredProductsForSelect(index);
                        
                        console.log(`Component ${index}:`, component);
                        console.log(`Available editions:`, availableEditions);
                        console.log(`Available products:`, availableProducts);
                        console.log(`Component type:`, component.type);
                        console.log(`Component product_id:`, component.product_id);
                        
                        return (
                          <div key={componentKey} className="flex items-center gap-3 p-3 bg-slate-50 rounded-lg">
                            <div className="flex-1 grid grid-cols-3 gap-3">
                              {/* Тип компонента */}
                              <div>
                                <Label className="text-xs text-slate-600">
                                  {t('product.component_type')}
                                </Label>
                                <Select
                                  value={component.type || 'book_edition'}
                                  onValueChange={(value) => {
                                    console.log(`Changing component ${index} type from ${component.type} to ${value}`);
                                    updateComponent(index, 'type', value);
                                    // Сбрасываем ID при смене типа
                                    updateComponent(index, 'item_id', '');
                                    updateComponent(index, 'product_id', '');
                                    console.log(`Component ${index} updated:`, components[index]);
                                  }}
                                >
                                  <SelectTrigger>
                                    <SelectValue />
                                  </SelectTrigger>
                                  <SelectContent>
                                    <SelectItem value="book_edition">{t('product.book_edition')}</SelectItem>
                                    <SelectItem value="simple_product">{t('product.simple_product')}</SelectItem>
                                  </SelectContent>
                                </Select>
                              </div>

                              {/* Выбор товара/книги */}
                              <div>
                                <Label className="text-xs text-slate-600">
                                  {component.type === 'book_edition' ? t('product.book_edition') : t('product.product_name')}
                                </Label>
                                
                                {component.type === 'book_edition' ? (
                                  <Select
                                                            value={component.item_id || ''}
                        onValueChange={(value) => updateComponent(index, 'item_id', value)}
                                  >
                                    <SelectTrigger>
                                      <SelectValue placeholder={t('placeholders.product.select_book_edition')} />
                                    </SelectTrigger>
                                    <SelectContent>
                                      {Object.entries(availableEditions).map(([bookTitle, bookData]) => (
                                        <div key={bookTitle}>
                                          <div className="px-2 py-1 text-xs font-medium text-slate-500 bg-slate-100">
                                            {bookTitle}
                                          </div>
                                          {bookData.editions.map(edition => (
                                            <SelectItem key={edition.id} value={edition.id}>
                                              {edition.display}
                                            </SelectItem>
                                          ))}
                                        </div>
                                      ))}
                                    </SelectContent>
                                  </Select>
                                ) : (
                                  <Select
                                    value={component.product_id || ''}
                                    onValueChange={(value) => updateComponent(index, 'product_id', value)}
                                  >
                                    <SelectTrigger>
                                      <SelectValue placeholder={t('placeholders.product.select_product')} />
                                    </SelectTrigger>
                                    <SelectContent>
                                      {availableProducts.map(product => (
                                        <SelectItem key={product.id} value={product.id}>
                                          {product.name} ({product.sku || 'No SKU'})
                                        </SelectItem>
                                      ))}
                                    </SelectContent>
                                  </Select>
                                )}
                              </div>

                              {/* Количество */}
                              <div>
                                <Label className="text-xs text-slate-600">
                                  {t('product.quantity')}
                                </Label>
                                <Input
                                  type="number"
                                  min="1"
                                  value={component.quantity || 1}
                                  onChange={(e) => updateComponent(index, 'quantity', parseInt(e.target.value) || 1)}
                                  className="w-full"
                                />
                              </div>
                            </div>
                            
                            <Button
                              type="button"
                              variant="ghost"
                              size="icon"
                              onClick={() => removeComponent(index)}
                              className="text-red-500 hover:text-red-700"
                            >
                              <X className="w-4 h-4" />
                            </Button>
                          </div>
                        );
                      })}
                    </div>
                  )}
                </div>
              </CardContent>
            </Card>
          )}

          {/* Кнопки */}
          <div className="flex justify-end gap-3 pt-6 border-t">
            <Button type="button" variant="outline" onClick={onClose}>
              {t('actions.cancel')}
            </Button>
            <Button
              type="submit"
              disabled={isSubmitting || !formData.name || !formData.price}
              className="bg-amber-600 hover:bg-amber-700"
            >
              {isSubmitting ? t('actions.saving') : (product ? t('actions.save') : t('actions.create'))}
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
}
